import { useCallback, useEffect, useRef, useState } from "react";
import { widgetEnvelopes } from "../../streaming/widgetEnvelopes";
import { WIDGET_SUBMIT_CODE } from "../../components/Messages/widgets/kinds";

/**
 * How long a submit waits for its outcome before the widget is operable again. The POST answers 200
 * once published and carries no verdict (WM04); a lost POST is Chat's SendFailed row (Retry re-sends
 * the same `meta`), and a second submit after this is answered `WIDGET_ALREADY_SUBMITTED` at worst.
 */
export const SUBMIT_SETTLE_MS = 15000;

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Submit a Tier-1 widget (spec-widget-mcp 05 Step 7; WM04 contract). The values go out on the
 * widget's ordinary query body as `meta.widgetSubmit { recordId, kind, values }` through `send`
 * (Chat's `handleRequest`, so the body, the token, the correlation id and Retry are the composer's).
 * The verdict comes back on the socket:
 *   - accepted → `record_update_stream.widget` with `state:'submitted'` (the renderer collapses);
 *   - `WIDGET_SUBMIT_INVALID` → `rejection.fields` (`{ <field>: message }`, `_form` for the whole form);
 *   - `WIDGET_ALREADY_SUBMITTED` → `collapse: 'submitted'`; `WIDGET_RECORD_GONE` → `collapse: 'expired'`.
 * Only an outcome that arrives after this hook's own submit is read.
 *
 * @param {{ recordId: string, kind: string, conversationId?: string|null,
 *   send?: (content: object, files: string[], options: object) => Promise<unknown>, store?: object }} args
 */
export function useWidgetSubmit({ recordId, kind, conversationId = null, send, store = widgetEnvelopes }) {
	const [status, setStatus] = useState("idle");
	const [rejection, setRejection] = useState(null);
	const [collapse, setCollapse] = useState(null);
	const [sentValues, setSentValues] = useState(null);
	const sinceRef = useRef(Infinity);
	const sendingRef = useRef(false);
	const timerRef = useRef(null);

	useEffect(() => store.subscribe((id) => {
		if (id !== recordId) return;
		const outcome = store.outcome(recordId);
		if (!outcome || outcome.seq <= sinceRef.current) return;
		sinceRef.current = outcome.seq;
		clearTimeout(timerRef.current);
		sendingRef.current = false;
		setStatus("idle");
		if (outcome.code === WIDGET_SUBMIT_CODE.ALREADY_SUBMITTED) return setCollapse("submitted");
		if (outcome.code === WIDGET_SUBMIT_CODE.RECORD_GONE) return setCollapse("expired");
		setRejection({ code: outcome.code, fields: isPlainObject(outcome.fields) ? outcome.fields : {} });
	}), [recordId, store]);

	useEffect(() => () => clearTimeout(timerRef.current), []);

	const submit = useCallback(async (values) => {
		if (typeof send !== "function" || sendingRef.current) return false;
		sendingRef.current = true;
		sinceRef.current = store.currentSeq();
		setStatus("sending");
		setRejection(null);
		setSentValues(values);
		clearTimeout(timerRef.current);
		timerRef.current = setTimeout(() => {
			sendingRef.current = false;
			setStatus((s) => (s === "sending" ? "idle" : s));
		}, SUBMIT_SETTLE_MS);
		await send(
			{ conversationId: conversationId || null },
			[],
			{ meta: { widgetSubmit: { recordId, kind, values } } },
		);
		return true;
	}, [send, store, recordId, kind, conversationId]);

	return { submit, status, rejection, collapse, sentValues, canSubmit: typeof send === "function" };
}
